import { TFunction } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import type { LearningActivity } from "types";
import { PollingData } from "../../components";
import {
  useEditionsQuery,
  useEnrollmentStatusQuery,
  useInvalidateEditionsQuery,
  usePatchMutation } from "../../queries";
import { useAuthStore } from "../../stores/authStore";
import { getError } from "../../utils/error";
import { useRouteParamsLo } from "../../utils/useRouteParamsLo";

// Polling storage utilities
const EDITION_CHANGE_POLLING_KEY = "edition_change_polling";

const clearEditionChangePollingFromStorage = () => {
  sessionStorage.removeItem(EDITION_CHANGE_POLLING_KEY);
};

const getEditionChangePollingFromStorage = (): PollingData | null => {
  const data = sessionStorage.getItem(EDITION_CHANGE_POLLING_KEY);
  return data ? JSON.parse(data) : null;
};

export type EditionFeedback = {
  messageKey?: string;
  rawMessage?: string;
  iconType: "success" | "error";
};

// Risolve il testo del feedback nel componente, dove vive `t`.
export const getFeedbackText = (feedback: EditionFeedback, t: TFunction): string =>
  feedback.rawMessage ?? t(feedback.messageKey ?? "generic_error");

type EditionChangeErrorOutcome = {
  messageKey?: string;
  rawMessage?: string;
  shouldRefetch: boolean;
};

// map error after edition change
const handleEditionChangeError = (error: unknown): EditionChangeErrorOutcome => {
  const parsedError = getError(error);
  const messageError = parsedError?.[0]?.message ?? "";

  if (messageError.includes("MAX_EDITION_CAPACITY_REACHED_DURING_UPDATE_ENROLLMENTS")) {
    return { messageKey: "max-capacity-reached-error", shouldRefetch: true };
  }

  if (parsedError?.[0]?.name === "FATAL") {
    //@ts-ignore
    const errorKey = JSON.parse(error as string)?.errors["FATAL"]?.[0]?.key;
    const fatalMessageKeys: Record<string, string> = {
      "bad.request.exception": "Status 400 - Bad request", //TODO tradurre?
      "bad.request.user.already.associated": "already-associated-error"
    };
    if (fatalMessageKeys[errorKey]) {
      return { messageKey: fatalMessageKeys[errorKey], shouldRefetch: false };
    }
    // fallback: messaggio grezzo dal backend, oppure errore generico
    return messageError
      ? { rawMessage: messageError, shouldRefetch: false }
      : { messageKey: "generic_error", shouldRefetch: false };
  }

  if (messageError.includes("participants maximum number")) {
    return { messageKey: "maximum-participants-error", shouldRefetch: false };
  }

  if (messageError.includes("already associated with user")) {
    return { messageKey: "already-associated-error", shouldRefetch: false };
  }

  // default generic error
  return { messageKey: "enrollment-generic-error", shouldRefetch: false };
};

export const useEditions = (
  learningObject: LearningActivity | null
) => {

  const { id, learningObjectTypology } = learningObject ?? {};

  const {
    idString
  } = useRouteParamsLo();

  const pollingCompletedRef = useRef<boolean>(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number>(0);

  const userId = useAuthStore(state => state.userData?.id_user) ?? 0;
  const corporateId = useAuthStore(state => state.userData?.organization_id);

  const [newEditionIdForPolling, setNewEditionIdForPolling] = useState<number | null>(null);
  const [isPollingEditionChange, setIsPollingEditionChange] = useState(false);
  const [feedback, setFeedback] = useState<EditionFeedback | null>(null); //response error feedback 

  const {
    data: editions ,
    refetch: refetchEditions,
    isFetching: editionsIsFetching
  } = useEditionsQuery({
    classroomId: id?.toString() ?? ""
  });

  const {
    mutate: changeEditionMutate,
    isPending: isChangingEdition
  } = usePatchMutation({
    isToj: learningObject?.isToj ?? false,
    learningObjectId: id ?? 0,
    learningObjectTypology: learningObjectTypology ?? ""
  });

  const { invalidateEditions } = useInvalidateEditionsQuery({
    classroomId: idString  ?? "",
    scrollToTop: false
  });

  // Enrollment status query for edition change polling
  const {
    data: editionChangeStatusData,
    dataUpdatedAt: editionChangeStatusUpdatedAt,
    refetch: refetchEditionChangeStatus
  } = useEnrollmentStatusQuery({
    corporateId: corporateId ?? 0,
    editionId: newEditionIdForPolling ?? 0,
    enabled: false, // Disabled, use manual refetch only
    userId
  });

  const saveEditionChangePollingToStorage = (editionId: number, userId: number) => {
    sessionStorage.setItem(EDITION_CHANGE_POLLING_KEY, JSON.stringify({
      editionId,
      startTime: Date.now(),
      userId
    }));
  };

  // set api response feedback, if needed refetch editions
  const notify = useCallback((newFeedback: EditionFeedback, refetch = false) => {
    setFeedback(newFeedback);
    invalidateEditions();
    if (refetch) refetchEditions();
  }, [invalidateEditions, refetchEditions]);

  // clear response feedback 
  const clearFeedback = useCallback(() => setFeedback(null), []);

  const handleEditionChange = useCallback((newEditionId: number) => {
    changeEditionMutate(newEditionId, {
      onError: (error) => {
        const { messageKey, rawMessage, shouldRefetch } = handleEditionChangeError(error);
        notify({ iconType: "error", messageKey, rawMessage }, shouldRefetch);
      },
      onSuccess: () => {

        // Set the new edition ID for polling
        setNewEditionIdForPolling(newEditionId);

        // Reset polling completed flag
        pollingCompletedRef.current = false;
        pollingStartTimeRef.current = Date.now();
  
        // Save to storage and start polling
        saveEditionChangePollingToStorage(newEditionId, userId);
        setIsPollingEditionChange(true);

        // Invalidate to prepare for updates
        invalidateEditions();
        refetchEditions();
      }
    });
  }, [changeEditionMutate, invalidateEditions, refetchEditions, notify, userId]);

  // Polling logic with progressive intervals
  useEffect(() => {
    if (!isPollingEditionChange) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      return;
    }

    const startTime = Date.now();
    const MAX_POLLING_DURATION = 120000; // 2 minutes
    const MAX_INTERVAL = 20000; // 20 seconds
    let currentInterval = 5000; // Start with 5 seconds

    const scheduleNextPoll = () => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= MAX_POLLING_DURATION) {
        // Timeout reached
        pollingCompletedRef.current = true;
        setIsPollingEditionChange(false);
        clearEditionChangePollingFromStorage();
        // invalidateLearningObject();
        notify({ iconType: "error", messageKey: "enrollment-polling-error" }, true);
        return;
      }

      // Refetch enrollment status
      refetchEditionChangeStatus();

      // Increment interval by 5s if below max
      if (currentInterval < MAX_INTERVAL) {
        currentInterval += 5000;
      }

      // Schedule next poll
      pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);
    };

    // First call after initial interval (5s)
    pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);

    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [isPollingEditionChange, refetchEditionChangeStatus, notify]);

  // Handle edition change status response
  useEffect(() => {
    if (!editionChangeStatusData?.status) return;

    // If polling is already completed, do nothing
    if (pollingCompletedRef.current) return;

    // Only process status changes if polling is active
    if (!isPollingEditionChange) return;

    
    if (editionChangeStatusUpdatedAt < pollingStartTimeRef.current) return;

    const currentStatus = editionChangeStatusData.status;
    const currentErrorMessage = editionChangeStatusData.errorMessage;

    if (currentStatus === "IN_PROGRESS") {
      // Already polling, continue
      // Status is still in progress, keep polling
    } else if (currentStatus === "COMPLETE" || currentStatus === "ERROR") {
      // Mark polling as completed FIRST to avoid loops
      pollingCompletedRef.current = true;
      // Stop polling
      setIsPollingEditionChange(false);

      // Remove from sessionStorage
      clearEditionChangePollingFromStorage();

      // Show modal with result
      // invalidateLearningObject();
      if (currentStatus === "COMPLETE") {
        notify({ iconType: "success", messageKey: "enrollment-submitted-success" }, true);
      } else if (currentStatus === "ERROR" && currentErrorMessage?.includes("[GET-ENROLLMENT-STATUS-ERROR]")){
        notify({ iconType: "error", messageKey: "max-capacity-reached-error" }, true);
      } else {
        notify({ iconType: "error", messageKey: "enrollment-polling-error" }, true);
      }
    }
    // NOT_ENROLLED: do nothing, normal behavior
  }, [
    editionChangeStatusData?.status,
    editionChangeStatusUpdatedAt,
    isPollingEditionChange,
    invalidateEditions,
    refetchEditions,
    editionChangeStatusData?.errorMessage
  ]);

  // Restore polling from sessionStorage on mount
  useEffect(() => {
    const savedPolling = getEditionChangePollingFromStorage();
    if (savedPolling && savedPolling.userId === userId) {
      // Resume polling with the saved edition ID
      setNewEditionIdForPolling(savedPolling.editionId);
      pollingCompletedRef.current = false;
      pollingStartTimeRef.current = savedPolling.startTime;
      setIsPollingEditionChange(true);
    }
  }, [userId]);

  return {
    clearFeedback,
    editions,
    editionsIsFetching,
    feedback,
    handleEditionChange,
    isChangingEdition,
    isPollingEditionChange,
    refetchEditions
  };
};
